import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';
import { submitApplication } from '../data/mock';

export const ApplicationModal = ({ isOpen, onClose, onPaymentRequired }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1
    fullName: '',
    email: '',
    phone: '',
    location: '',
    // Step 2
    currentProfession: '',
    wellnessExperience: '',
    education: '',
    whyWellness: '',
    // Step 3
    careerGoals: '',
    incomeGoals: '',
    timeCommitment: '',
    investmentReady: ''
  });

  const totalSteps = 3;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRadioChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateStep = (step) => {
    if (step === 1) {
      return formData.fullName && formData.email && formData.phone && formData.location;
    }
    if (step === 2) {
      return formData.currentProfession && formData.wellnessExperience && formData.education && formData.whyWellness;
    }
    if (step === 3) {
      return formData.careerGoals && formData.incomeGoals && formData.timeCommitment && formData.investmentReady;
    }
    return false;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await submitApplication(formData);
      
      if (response.success) {
        toast.success('Application submitted successfully!', { duration: 4000 });
        
        // Store applicant data for payment modal
        const applicantData = {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone
        };
        
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          location: '',
          currentProfession: '',
          wellnessExperience: '',
          education: '',
          whyWellness: '',
          careerGoals: '',
          incomeGoals: '',
          timeCommitment: '',
          investmentReady: ''
        });
        setCurrentStep(1);
        onClose();
        
        // Trigger payment modal with applicant data
        if (onPaymentRequired) {
          setTimeout(() => {
            onPaymentRequired(response.applicationId, applicantData);
          }, 500);
        }
      }
    } catch (error) {
      const errorMessage = error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Apply for Wellness Coach Academy
          </DialogTitle>
          <DialogDescription>
            Complete the application to be considered for our exclusive program
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    currentStep >= step
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                </div>
                <span className="text-xs mt-2 text-center">
                  {step === 1 && 'Basic Info'}
                  {step === 2 && 'Background'}
                  {step === 3 && 'Goals'}
                </span>
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-colors ${
                    currentStep > step ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className="focus-ring"
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                className="focus-ring"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 98765 43210"
                className="focus-ring"
              />
            </div>
            <div>
              <Label htmlFor="location">Location (City, Country) *</Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Mumbai, India"
                className="focus-ring"
              />
            </div>
          </div>
        )}

        {/* Step 2: Background & Experience */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="currentProfession">Current Profession *</Label>
              <Input
                id="currentProfession"
                name="currentProfession"
                value={formData.currentProfession}
                onChange={handleInputChange}
                placeholder="e.g., Marketing Manager, Yoga Teacher, etc."
                className="focus-ring"
              />
            </div>
            <div>
              <Label htmlFor="wellnessExperience">Wellness Industry Experience *</Label>
              <RadioGroup
                value={formData.wellnessExperience}
                onValueChange={(value) => handleRadioChange('wellnessExperience', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="exp-none" />
                  <Label htmlFor="exp-none">No experience</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="beginner" id="exp-beginner" />
                  <Label htmlFor="exp-beginner">Beginner (0-2 years)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="intermediate" id="exp-intermediate" />
                  <Label htmlFor="exp-intermediate">Intermediate (2-5 years)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="advanced" id="exp-advanced" />
                  <Label htmlFor="exp-advanced">Advanced (5+ years)</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="education">Highest Education Level *</Label>
              <Input
                id="education"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                placeholder="e.g., Bachelor's in Psychology"
                className="focus-ring"
              />
            </div>
            <div>
              <Label htmlFor="whyWellness">Why are you interested in the wellness industry? *</Label>
              <Textarea
                id="whyWellness"
                name="whyWellness"
                value={formData.whyWellness}
                onChange={handleInputChange}
                placeholder="Share your motivation and passion..."
                rows={4}
                className="focus-ring"
              />
            </div>
          </div>
        )}

        {/* Step 3: Goals & Commitment */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="careerGoals">Career Goals (1 year from now) *</Label>
              <Textarea
                id="careerGoals"
                name="careerGoals"
                value={formData.careerGoals}
                onChange={handleInputChange}
                placeholder="What do you want to achieve professionally?"
                rows={3}
                className="focus-ring"
              />
            </div>
            <div>
              <Label htmlFor="incomeGoals">Income Goals *</Label>
              <RadioGroup
                value={formData.incomeGoals}
                onValueChange={(value) => handleRadioChange('incomeGoals', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3-5L" id="income-3-5" />
                  <Label htmlFor="income-3-5">₹3-5 Lakhs/year</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="5-10L" id="income-5-10" />
                  <Label htmlFor="income-5-10">₹5-10 Lakhs/year</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="10-20L" id="income-10-20" />
                  <Label htmlFor="income-10-20">₹10-20 Lakhs/year</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="20L+" id="income-20plus" />
                  <Label htmlFor="income-20plus">₹20+ Lakhs/year</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="timeCommitment">Time Commitment (weekly) *</Label>
              <RadioGroup
                value={formData.timeCommitment}
                onValueChange={(value) => handleRadioChange('timeCommitment', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="5-10" id="time-5-10" />
                  <Label htmlFor="time-5-10">5-10 hours/week</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="10-15" id="time-10-15" />
                  <Label htmlFor="time-10-15">10-15 hours/week</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="15-20" id="time-15-20" />
                  <Label htmlFor="time-15-20">15-20 hours/week</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="20+" id="time-20plus" />
                  <Label htmlFor="time-20plus">20+ hours/week</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="investmentReady">Investment Readiness *</Label>
              <RadioGroup
                value={formData.investmentReady}
                onValueChange={(value) => handleRadioChange('investmentReady', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ready" id="ready" />
                  <Label htmlFor="ready">Ready to invest immediately</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1month" id="1month" />
                  <Label htmlFor="1month">Ready within 1 month</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3months" id="3months" />
                  <Label htmlFor="3months">Need 1-3 months</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="exploring" id="exploring" />
                  <Label htmlFor="exploring">Still exploring options</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="focus-ring"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              className="btn-primary-green btn-transition focus-ring"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary-purple btn-transition focus-ring"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
